//#region Props

import { Contract } from "../../../../models/Entities/Contracts/Contract";
import { ContractAddendum } from "../../../../models/Entities/Contracts/ContractAddendum";
import { ErrorType } from "../../../../models/HttpError";
import { AppState } from "../../../../redux/reducers";

// stores
import * as ContractsStore from "../../../../redux/middleware/ContractMiddleware";
import * as ContractAddendumsStore from "../../../../redux/middleware/ContractAddendum.Middleware";


interface IProps {
    ContractId: number;
    OnAddendumSelected: (addendum: ContractAddendum) => void;
    // Adenda a seleccionar al entrar (p. ej. ?addendumId= después de crear una con IA, spec 012 US9)
    InitialAddendumId?: number;
}

interface ConnectedProps {
    isLoading: boolean;
    isLoadingSuccessfully: boolean | undefined;
    failOnLoading: boolean;
    isCreatingAddendum: boolean;
    isCreatingAddendumSuccessfully: boolean | undefined;
    failOnCreatingAddendum: boolean;
    isGettingAddendums: boolean;
    ObteinedAddendumsSuccessfully: boolean | undefined;
    FailGettingAddendums: boolean;
    error: ErrorType | undefined;
    Contract: Contract | undefined;
    Addendums: ContractAddendum[] | undefined;
    NegociationAddendums: ContractAddendum[] | undefined;
}

interface MedicalServicesDispatchProps {
    GetContractById: typeof ContractsStore.actionCreators.GetContractById;
    GetContractAddendumsByContractId: typeof ContractAddendumsStore.actionCreators.GetContractAddendumsByContractId;
    CreateContractAddendum: typeof ContractAddendumsStore.actionCreators.CreateContractAddendum;
    UpdateContractAddendum: typeof ContractAddendumsStore.actionCreators.UpdateContractAddendum;
    DeleteContractAddendum: typeof ContractAddendumsStore.actionCreators.DeleteContractAddendum;
    CloneAddendum: typeof ContractAddendumsStore.actionCreators.CloneAddendum;
    SetContractAddendumEnabled: typeof ContractAddendumsStore.actionCreators.SetContractAddendumEnabled;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const currentAddendums = state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.addendums || [];

    const getExpiryStartTime = (addendum: ContractAddendum): number => {
        const value = addendum?.contractAddendumExpiryStartDate;
        if (!value) {
            return Number.MAX_SAFE_INTEGER;
        }

        const dateValue = value instanceof Date ? value : new Date(value);
        const timestamp = dateValue.getTime();

        return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
    };

    const hasNoExpiryDates = (addendum: ContractAddendum) => !addendum?.contractAddendumExpiryStartDate && !addendum?.contractAddendumExpiryEndDate;

    const sortedAddendums = currentAddendums.length > 0
        ? [...currentAddendums].sort((a, b) => {
            const aNoExpiry = hasNoExpiryDates(a);
            const bNoExpiry = hasNoExpiryDates(b);

            // If both have no expiry dates, sort by id ascending
            if (aNoExpiry && bNoExpiry) {
                return (a.id ?? 0) - (b.id ?? 0);
            }

            // Otherwise, keep existing logic by start date (missing start goes to the end via MAX_SAFE_INTEGER)
            const aStart = getExpiryStartTime(a);
            const bStart = getExpiryStartTime(b);
            return aStart - bStart;
        })
        : [];

    return {
        isLoading: state.Contract?.isLoadingOne,
        isLoadingSuccessfully: state.Contract?.successLoadingOne,
        failOnLoading: state.Contract?.failOnLoadingOne,
        isCreatingAddendum: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.isCloning,
        isCreatingAddendumSuccessfully: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.isClonedSuccessfully,
        failOnCreatingAddendum: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.failOnCloning,
        isGettingAddendums: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.isGettingContractAddendums,
        ObteinedAddendumsSuccessfully: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.isGettingContractAddendumsSuccess,
        FailGettingAddendums: state.Contract?.list?.find(c => c.id === ownProps.ContractId)?.FailGettingContractAddendums,
        error: state.Contract?.error,
        Contract: state.Contract?.list?.find(c => c.id === ownProps.ContractId),
        Addendums: sortedAddendums.filter(a => a.contractAddendumType === 0),
        NegociationAddendums: sortedAddendums.filter(a => a.contractAddendumType === 1)
    }
};

export const mapDispatchToProps = {
    ...ContractsStore.actionCreators,
    ...ContractAddendumsStore.actionCreators
};

//#endregion Props

export const isCurrentAddendum = (addendum: ContractAddendum | undefined) => {
    const today = new Date();
    if (!addendum) return false;
    if (!addendum.contractAddendumExpiryStartDate && !addendum.contractAddendumExpiryEndDate) return false;
    if (addendum.contractAddendumExpiryStartDate && !addendum.contractAddendumExpiryEndDate) {
        return today >= new Date(addendum.contractAddendumExpiryStartDate);
    }
    if (!addendum.contractAddendumExpiryStartDate && addendum.contractAddendumExpiryEndDate) {
        return today <= new Date(addendum.contractAddendumExpiryEndDate);
    }
    return today >= new Date(addendum.contractAddendumExpiryStartDate!) && today <= new Date(addendum.contractAddendumExpiryEndDate!);
}

export const isExpiredAddendum = (addendum: ContractAddendum | undefined) => {
    const today = new Date();
    if (!addendum) return false;
    if (!addendum.contractAddendumExpiryStartDate && !addendum.contractAddendumExpiryEndDate) return false;
    if (addendum.contractAddendumExpiryStartDate && !addendum.contractAddendumExpiryEndDate) {
        return false;
    }
    if (!addendum.contractAddendumExpiryStartDate && addendum.contractAddendumExpiryEndDate) {
        return today > new Date(addendum.contractAddendumExpiryEndDate);
    }

    if (addendum.contractAddendumExpiryStartDate && addendum.contractAddendumExpiryEndDate) {
        return !(today < new Date(addendum.contractAddendumExpiryStartDate))
    } 

    return today > new Date(addendum.contractAddendumExpiryEndDate!);
}



export const GetContractStatusClass = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return "";
        case 1:
            return "Active";
        case 2:
            return "Inactive";
        case 3:
            return "Suspended";
        case 4:
            return "Expired";
        case 5:
            return "Canceled";
        case 6:
            return "Terminated";
        default:
            return "";
    }
};

export const GetContractStatusDescripcion = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return "Undefined";
        case 1:
            return "Active";
        case 2:
            return "Inactive";
        case 3:
            return "Suspended";
        case 4:
            return "Expired";
        case 5:
            return "Canceled";
        case 6:
            return "Finished";
        default:
            return "Undefined";
    }
};

// Selección inicial: la pedida por InitialAddendumId si existe; si no, la vigente; si no, la primera.
export const pickInitialAddendum = (addendums: ContractAddendum[] | undefined, initialAddendumId?: number): ContractAddendum | undefined => {
    if (!addendums?.length) return undefined;
    const requested = initialAddendumId !== undefined ? addendums.find(a => a.id === initialAddendumId) : undefined;
    return requested ?? addendums.find(isCurrentAddendum) ?? addendums[0];
};
